import React from 'react';
import { flow, map, join, sumBy, get } from 'lodash/fp';
import { Table, IconButton, Icon } from 'rsuite';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import * as PERMISSIONS from '../../../../constants/permissions/inventory';
import { modifyAccess, fullAccess } from '../../../../helpers/permission';
import AccessControl from '../../../AccessControl';
import IconLink from '../../../IconLink';
import InventoryActions from '../../../../constants/inventory/InventoryActions';
import IconByAdjustAction from '../../../../constants/inventory/IconByAdjustAction';
import {
  ItemType,
  PackageType
} from '../../../../constants/inventory/InventoryTypes';
import createItemPathFor from '../../../../helpers/createItemPathFor';
import InventoryImage from '../../Image';
import Link from '../../../Link';
import {
  InventoryItemType,
  IventoryPackageItemType
} from '../../../../interfaces/inventory';

const { Cell } = Table;
const { ADD, REMOVE } = InventoryActions;

const quantityAvailable = sumBy('quantity');
const permissionByType = {
  [ItemType]: PERMISSIONS.ITEM,
  [PackageType]: PERMISSIONS.PACKAGE
};
const hasQuantityAvailable = ({ availability }) =>
  quantityAvailable(availability) > 0;

const ImageWrapper = styled.div`
  width: 40px;
  height: 40px;
  background: #f5f5f5;
  border-radius: 20px;
  margin-top: -10px;
  overflow: hidden;
  display: inline-block;
`;

export const ActionCell = ({ rowData, handleDelete, dataKey, ...props }) => {
  const hasQty = hasQuantityAvailable(rowData);
  const isItemType = rowData.itemType === ItemType;
  const permission = get(rowData.itemType, permissionByType);

  return (
    <Cell {...props} className="link-group">
      {isItemType && (
        <AccessControl allowOnlyFor={modifyAccess(permission)}>
          <IconLink
            icon={IconByAdjustAction[ADD]}
            to={createItemPathFor('adjust', rowData, { action: ADD })}
          />
        </AccessControl>
      )}
      <AccessControl allowOnlyFor={modifyAccess(permission)}>
        <IconLink
          icon={IconByAdjustAction[REMOVE]}
          to={createItemPathFor('adjust', rowData, { action: REMOVE })}
          disabled={!hasQty}
        />
      </AccessControl>
      <IconLink icon="eye" to={createItemPathFor('view', rowData)} />
      <AccessControl allowOnlyFor={modifyAccess(permission)}>
        <IconLink icon="edit2" to={createItemPathFor('edit', rowData)} />
      </AccessControl>
      <AccessControl allowOnlyFor={fullAccess(permission)}>
        <IconButton
          style={{ top: '-8px', paddingLeft: 18 }}
          appearance="link"
          color="red"
          onClick={() => handleDelete(rowData)}
          icon={<Icon icon="trash" style={{ width: 32 }} />}
          disabled={isItemType && hasQty}
        />
      </AccessControl>
    </Cell>
  );
};

ActionCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  rowData: PropTypes.oneOfType([InventoryItemType, IventoryPackageItemType]),
  handleDelete: PropTypes.func.isRequired
};

ActionCell.defaultProps = {
  rowData: null
};

export const TagsCell = ({ rowData, dataKey, ...props }) => (
  <Cell {...props} className="link-group">
    {flow(
      map('name'),
      join(', ')
    )(rowData[dataKey])}
  </Cell>
);

TagsCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  rowData: PropTypes.oneOfType([InventoryItemType, IventoryPackageItemType])
};

TagsCell.defaultProps = {
  rowData: null
};

export const QtyAvailableCell = ({ rowData, dataKey, ...props }) => (
  <Cell {...props} className="link-group">
    {quantityAvailable(rowData[dataKey])}
  </Cell>
);

QtyAvailableCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  rowData: PropTypes.oneOfType([InventoryItemType, IventoryPackageItemType])
};

QtyAvailableCell.defaultProps = {
  rowData: null
};

export const LinkCell = ({ rowData, dataKey, ...props }) => (
  <Cell {...props}>
    <Link to={createItemPathFor('view', rowData)}>{rowData[dataKey]}</Link>
  </Cell>
);

LinkCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  rowData: PropTypes.oneOfType([InventoryItemType, IventoryPackageItemType])
};

LinkCell.defaultProps = {
  rowData: null
};

export const ImageLinkCell = ({ rowData, dataKey, ...props }) => (
  <Cell {...props} style={{ padding: 0 }}>
    <Link to={createItemPathFor('view', rowData)}>
      <ImageWrapper>
        <InventoryImage image={get(dataKey, rowData)} style={{ width: 40 }} />
      </ImageWrapper>
    </Link>
  </Cell>
);

ImageLinkCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  rowData: PropTypes.oneOfType([InventoryItemType, IventoryPackageItemType])
};

ImageLinkCell.defaultProps = {
  rowData: null
};
